import { useMemo, useState } from 'react';
import {
  Alert,
  Card,
  Chip,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchRounded from '@mui/icons-material/SearchRounded';
import { api } from '@/api';
import { nomeProprio } from '@/features/notas/texto';
import { useAsync } from '@/hooks/useAsync';

interface EmailEnviado {
  id: number;
  tipo: string;
  para: string;
  assunto: string;
  status: string;
  erro: string | null;
  enviado_em: string;
  ra: string | null;
  aluno: string | null;
}

const ROTULO_TIPO: Record<string, string> = {
  convite: 'Convite (P1)',
  confirmacao: 'Confirmação do RA',
  teste: 'Teste',
};
const semAcento = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

/** Histórico de tudo o que o sistema enviou por e-mail. */
export function EmailsAba() {
  const { data, error } = useAsync(
    (signal) => api.get<EmailEnviado[]>('/admin/emails', { signal }),
    [],
  );
  const [busca, setBusca] = useState('');
  const [tipo, setTipo] = useState('todos');
  const lista = useMemo(() => {
    const q = semAcento(busca.trim());
    return (data ?? []).filter(
      (e) =>
        (tipo === 'todos' || e.tipo === tipo) &&
        (!q || semAcento(`${e.aluno ?? ''} ${e.para} ${e.ra ?? ''}`).includes(q)),
    );
  }, [data, busca, tipo]);

  if (error) return <Alert severity="error">Não consegui carregar os e-mails.</Alert>;
  const falhas = (data ?? []).filter((e) => e.status !== 'enviado').length;

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ alignItems: { md: 'center' } }}
      >
        <TextField
          size="small"
          placeholder="Filtrar por aluno, e-mail ou RA"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          sx={{ minWidth: 300 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded />
                </InputAdornment>
              ),
            },
          }}
        />
        <ToggleButtonGroup size="small" exclusive value={tipo} onChange={(_, v) => v && setTipo(v)}>
          <ToggleButton value="todos">Todos</ToggleButton>
          <ToggleButton value="convite">Convites</ToggleButton>
          <ToggleButton value="confirmacao">Confirmações</ToggleButton>
          <ToggleButton value="teste">Testes</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="body2" color="text.secondary">
          {lista.length} e-mails{falhas ? ` · ${falhas} com falha` : ''}
        </Typography>
      </Stack>
      <Card>
        <TableContainer sx={{ maxHeight: 700 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Enviado em</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Para</TableCell>
                <TableCell>Assunto</TableCell>
                <TableCell>Situação</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lista.map((e) => (
                <TableRow key={e.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {new Date(e.enviado_em).toLocaleString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </TableCell>
                  <TableCell>{ROTULO_TIPO[e.tipo] ?? e.tipo}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{e.aluno ? nomeProprio(e.aluno) : '–'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {e.para}
                    </Typography>
                  </TableCell>
                  <TableCell>{e.assunto}</TableCell>
                  <TableCell>
                    {e.status === 'enviado' ? (
                      <Chip size="small" color="success" variant="outlined" label="Enviado" />
                    ) : (
                      <Tooltip title={e.erro ?? ''}>
                        <Chip size="small" color="error" variant="outlined" label="Falhou" />
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Stack>
  );
}
